import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * Tour — a few stops around a screen, each a small bubble pointing at the thing it explains, built on the kit's popover
 * (Radix's) rather than a tour library.
 *
 * What it promises, so the caller does not have to:
 *
 *   - **It is a dialog while it is open.** The bubble is modal: focus moves into it on every stop (its primary button
 *     takes it), Tab stays inside it, and the rest of the page is out of reach and out of the accessibility tree until
 *     the tour ends. The bubble is named by its stop's title and described by its sentence, and says where it is
 *     ("2 of 7") in words, so it reads the same aloud.
 *   - **Every way out is there.** Next and Back, Skip (and the close button) on every stop, Escape at any time, and the
 *     arrow keys for Next and Back. Done on the last stop. Clicking outside does nothing, so a stray click does not
 *     lose a newcomer their place.
 *   - **Focus comes back** to wherever it was when the tour started.
 *   - **It skips what is not there.** A stop whose target is missing or hidden — a section the member's role does not
 *     list, a sidebar folded away on a phone — is left out when the tour opens. With no stop left, it does not open.
 *   - **Its target is marked**, with `data-tour-active` on the element while its stop is showing (the kit draws a
 *     ring), and scrolled into view.
 *   - **Motion and theme are the kit's**: it enters with the popover's short pop, scaled by `--clavian-motion-scale`
 *     (still under reduced motion), on the overlay surface of whichever theme is on.
 */
import * as RadixPopover from '@radix-ui/react-popover';
import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { useUiText } from '@clavian/i18n/react';
import { cx } from '../lib/cx.js';
import { usePortalContainer } from '../theme/theme-provider.js';
import { Button } from './button.js';
import { CloseIcon } from './icons.js';
/** The first element a selector matches that is actually shown: in the layout, not `display: none`. */
function shownTarget(selector) {
    if (typeof document === 'undefined')
        return null;
    for (const element of Array.from(document.querySelectorAll(selector))) {
        if (element.getClientRects().length > 0)
            return element;
    }
    return null;
}
export function Tour({ open, ...rest }) {
    // One session per opening: what is on screen is decided as it opens, and the place resets when it closes.
    return open ? _jsx(TourSession, { ...rest }) : null;
}
function TourSession({ stops, onClose, label, className }) {
    const t = useUiText();
    const container = usePortalContainer();
    const titleId = useId();
    const bodyId = useId();
    // The stops that have something to point at, and where focus was, both read once as the tour opens.
    const [shown] = useState(() => stops.filter((stop) => shownTarget(stop.target) !== null));
    const [returnTo] = useState(() => (typeof document === 'undefined' ? null : document.activeElement));
    const [index, setIndex] = useState(0);
    const primary = useRef(null);
    const stop = shown[index];
    const target = useMemo(() => (stop === undefined ? null : shownTarget(stop.target)), [stop]);
    // What the bubble is placed against: the target, as the popper's virtual anchor.
    const anchor = useMemo(() => ({ current: target }), [target]);
    // Mark the target and bring it into view while its stop is showing.
    useEffect(() => {
        if (target === null)
            return;
        target.setAttribute('data-tour-active', '');
        target.scrollIntoView?.({ block: 'nearest', inline: 'nearest' });
        return () => target.removeAttribute('data-tour-active');
    }, [target]);
    const end = useCallback((how) => onClose(how), [onClose]);
    // Focus goes back where it was once the bubble has gone — after the popover's own focus scope has let go, or it
    // would pull focus back into a bubble that is leaving.
    useEffect(() => () => {
        if (returnTo !== null && typeof window !== 'undefined')
            window.setTimeout(() => returnTo.focus?.(), 0);
    }, [returnTo]);
    // Opened with nothing on screen to point at: say it ended, rather than sit open with no bubble.
    useEffect(() => {
        if (shown.length === 0)
            onClose('skipped');
    }, [shown, onClose]);
    if (stop === undefined || target === null)
        return null;
    const last = index === shown.length - 1;
    const next = () => (last ? end('done') : setIndex((current) => Math.min(current + 1, shown.length - 1)));
    const back = () => setIndex((current) => Math.max(current - 1, 0));
    const onKeyDown = (event) => {
        if (event.key === 'ArrowRight') {
            event.preventDefault();
            next();
        }
        else if (event.key === 'ArrowLeft' && index > 0) {
            event.preventDefault();
            back();
        }
    };
    return (_jsxs(RadixPopover.Root, { open: true, modal: true, onOpenChange: (next) => (next ? undefined : end('skipped')), children: [_jsx(RadixPopover.Anchor, { virtualRef: anchor }), _jsx(RadixPopover.Portal, { container: container, children: _jsxs(RadixPopover.Content
                // One mount per stop, so each takes focus and is announced afresh.
                , { className: cx('clav-popover__content', 'clav-tour', className), side: stop.side ?? 'right', align: "center", sideOffset: 10, collisionPadding: 12, "aria-labelledby": titleId, "aria-describedby": bodyId, onOpenAutoFocus: (event) => {
                        event.preventDefault();
                        primary.current?.focus();
                    }, 
                    // Focus is returned by `end`, to where it was before the tour, not to the anchor.
                    onCloseAutoFocus: (event) => event.preventDefault(), onPointerDownOutside: (event) => event.preventDefault(), onInteractOutside: (event) => event.preventDefault(), onKeyDown: onKeyDown, "data-tour-stop": stop.id, children: [_jsxs("div", { className: "clav-tour__head", children: [_jsxs("p", { className: "clav-tour__count", children: [_jsxs("span", { className: "clav-visually-hidden", children: [label, ", "] }), t('tour.stopOf', { current: index + 1, total: shown.length })] }), _jsx("button", { type: "button", className: "clav-tour__close", "aria-label": t('tour.close'), onClick: () => end('skipped'), children: _jsx(CloseIcon, {}) })] }), _jsx("h2", { id: titleId, className: "clav-tour__title", children: stop.title }), _jsx("p", { id: bodyId, className: "clav-tour__body", children: stop.body }), _jsx("div", { className: "clav-tour__dots", "aria-hidden": "true", children: shown.map((one, at) => (_jsx("span", { className: "clav-tour__dot", "data-current": at === index || undefined }, one.id))) }), _jsxs("div", { className: "clav-tour__actions", children: [_jsx(Button, { variant: "ghost", size: "sm", onClick: () => end('skipped'), children: t('tour.skip') }), _jsx("span", { className: "clav-tour__spring" }), index > 0 ? (_jsx(Button, { variant: "secondary", size: "sm", onClick: back, children: t('tour.back') })) : null, _jsx(Button, { ref: primary, variant: "primary", size: "sm", onClick: next, children: last ? t('tour.done') : t('tour.next') })] }), _jsx(RadixPopover.Arrow, { className: "clav-tour__arrow", width: 14, height: 7 })] }, stop.id) })] }));
}
//# sourceMappingURL=tour.js.map